/* iceberg-ui */
import {
    Flex,
    Form,
    FormFieldGroup,
    FormModalBody,
    FormSelect
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Alert, Typography } from '@mui/material';

/* Shared */
import { Notify } from 'application/Shared/Helpers';
import { ModalFormFooterIceberg } from 'application/Shared/Modals/Components/ModalFormFooterIceberg';

/* Hooks */
import { useDeleteUserCustomTableView } from 'hooks/dataAccess/UserCustomTableView/userCustomTableView';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { useManagedTableV2Context } from '../../ManagedTableV2ContextProvider';
import {
    CustomTableContext,
    CustomTableView,
    ShowFlyoutMenuActionEnum
} from '../../types';

interface CustomViewFormValues {
    customTableViewsId: string;
}

interface DeleteWasSuccessful {
    customTableViewsId?: string;
    displayName?: string;
}

export const DeleteTableView = <T,>({
    'data-testid': dataTestId = 'deleteTableView'
}: {
    /**
     * The data test id for this table flyout component.
     */
    'data-testid'?: string;
}) => {
    const { t } = useTranslation();
    const [deleteWasSuccessful, setDeleteWasSuccessful] =
        useState<DeleteWasSuccessful>({});
    const {
        currentTableView,
        customTableViews,
        defaultView,
        refetchCustomTableViews,
        setCurrentTableView,
        setShowFlyoutMenu,
        tableName
    } = useManagedTableV2Context<T>() as CustomTableContext<T>;

    useEffect(() => {
        if (
            deleteWasSuccessful.displayName &&
            deleteWasSuccessful.customTableViewsId
        ) {
            Notify.s(
                t('TableViews.userCustomTableView.delete.success', {
                    displayName: deleteWasSuccessful.displayName
                })
            );
            setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
            if (
                currentTableView?.id === deleteWasSuccessful.customTableViewsId
            ) {
                setCurrentTableView(defaultView);
                return () => {
                    refetchCustomTableViews();
                };
            }
            setDeleteWasSuccessful({});
            return () => {};
        }
        return () => {};
    }, [
        currentTableView?.id,
        defaultView,
        deleteWasSuccessful.customTableViewsId,
        deleteWasSuccessful.displayName,
        refetchCustomTableViews,
        setCurrentTableView,
        setShowFlyoutMenu,
        t
    ]);

    const { deleteUserCustomTableView } = useDeleteUserCustomTableView(t);

    const options =
        customTableViews &&
        Object.values(customTableViews).map((view) => ({
            label: (view as CustomTableView).displayName,
            value: (view as CustomTableView).id
        }));

    return (
        <Flex flexDirection="column" data-testid={dataTestId} width="100%">
            <Alert
                severity="warning"
                icon={false}
                sx={{ margin: '22px', marginBottom: '0' }}
            >
                {t('TableViews.flyout.delete.warning')}
            </Alert>
            <Typography p="22px">
                {t('TableViews.flyout.delete.subtitle')}
            </Typography>
            <Form<CustomViewFormValues>
                defaultValues={{
                    customTableViewsId: currentTableView?.displayName
                }}
                onSubmit={async ({ customTableViewsId }) => {
                    const { data } = await deleteUserCustomTableView(
                        customTableViewsId
                    );

                    const displayName = options?.find(
                        (option: { label: string; value: string }) =>
                            option.value === customTableViewsId
                    )?.label;

                    if (data?.serverResponse === 200) {
                        setDeleteWasSuccessful({
                            customTableViewsId,
                            displayName
                        });
                    } else {
                        Notify.e(
                            t('TableViews.userCustomTableView.delete.error', {
                                displayName
                            })
                        );
                    }
                }}
                sx={{ flex: 1 }}
            >
                <Flex
                    flexDirection="column"
                    height="100%"
                    justifyContent="space-between"
                >
                    <FormModalBody>
                        <FormFieldGroup>
                            <FormSelect
                                label={t('TableViews.flyout.form.displayName')}
                                name="customTableViewsId"
                                options={options}
                                width="100%"
                            />
                        </FormFieldGroup>
                    </FormModalBody>
                    <ModalFormFooterIceberg
                        data-trackid={`deleteCustomView_${tableName}`}
                        onClose={() => {
                            setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
                        }}
                        onCloseBtnText={t('Common.cancel')}
                        onSubmitBtnText={t('Common.delete')}
                    />
                </Flex>
            </Form>
        </Flex>
    );
};
